import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLazyValidateQuery } from '../redux/slices/auth';
import { useIsAuthenticated } from 'services/useAuth';
import { useEncephalon } from 'contexts/EncephalonContext';

const TokenHandler: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [validate] = useLazyValidateQuery();
  const { initialized, isAuthenticated } = useIsAuthenticated();
  const { clearEncephalonState } = useEncephalon();

  useEffect(() => {
    if (!initialized) return;

    const handleToken = async () => {
      if (isAuthenticated) return;

      const query = new URLSearchParams(location.search);
      const token = query.get('t');
      const dicomId = query.get('dicom');

      if (!token) return;

      try {
        const isValid = await validate(token).unwrap();

        if (isValid) {
          // Clear all state before setting new token
          localStorage.removeItem('access_token');
          localStorage.removeItem('firebase_id_token');
          sessionStorage.removeItem('dicom_id'); // <-- use sessionStorage
          clearEncephalonState();

          localStorage.setItem('access_token', token);
          localStorage.setItem('firebase_id_token', token);

          if (dicomId) {
            sessionStorage.setItem('dicom_id', dicomId); // <-- use sessionStorage
          }

          const newSearch = dicomId ? `?dicom=${encodeURIComponent(dicomId)}` : '';
          window.history.replaceState({}, '', window.location.pathname + newSearch);

          window.dispatchEvent(new Event('storage'));

          const redirectPath = dicomId ? `/?dicom=${encodeURIComponent(dicomId)}` : '/';
          navigate(redirectPath, { replace: true });
        } else {
          throw new Error('Invalid token');
        }
      } catch (error) {
        console.error('Token validation failed:', error);
        localStorage.removeItem('access_token');
        localStorage.removeItem('firebase_id_token');
        localStorage.removeItem('dicom_id');
        clearEncephalonState();
        navigate('/login', { replace: true });
      }
    };

    handleToken();
  }, [location.search, initialized, isAuthenticated, navigate, validate, clearEncephalonState]);

  return null;
};

export default TokenHandler;
